import { useEffect, useMemo, useState } from 'react'
import { AdminLayout, useAdminAccess } from '../../components/AdminLayout'
import { useLanguage } from '../../contexts/LanguageContext'
import { requestApi } from '../../lib/adminApi'

type AIChannel = {
  id: number
  name: string
  api_key: string
  base_url: string
  tier?: string
  priority: number
  enabled: boolean
  is_official: boolean
  max_fail_count: number
  recovery_time: number
  description: string
  status?: string | null
  success_rate?: string | null
  total_requests?: number | null
  total_failures?: number | null
  avg_latency_ms?: number | null
}

type ChannelForm = {
  name: string
  apiKey: string
  baseUrl: string
  tier: string
  priority: string
  enabled: boolean
  isOfficial: boolean
  maxFailCount: string
  recoveryTime: string
  description: string
}

const createDefaultForm = (): ChannelForm => ({
  name: '',
  apiKey: '',
  baseUrl: '',
  tier: 'standard',
  priority: '1',
  enabled: true,
  isOfficial: false,
  maxFailCount: '2',
  recoveryTime: '300',
  description: ''
})

const AdminAIChannelsContent = () => {
  const { t } = useLanguage()
  const { isGranted } = useAdminAccess()
  const [channels, setChannels] = useState<AIChannel[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [form, setForm] = useState<ChannelForm>(createDefaultForm())
  const [editing, setEditing] = useState<AIChannel | null>(null)
  const [alert, setAlert] = useState<string>('')
  const [submitting, setSubmitting] = useState(false)
  const [lastUpdated, setLastUpdated] = useState<string>('')

  const refreshChannels = () => {
    setLoading(true)
    setError('')
    requestApi<{ channels: AIChannel[] }>('/api/v1/admin/ai-channels')
      .then((payload) => {
        setChannels(payload.channels || [])
        setLastUpdated(new Date().toLocaleTimeString())
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : t('admin.aiChannels.error'))
      })
      .finally(() => {
        setLoading(false)
      })
  }

  useEffect(() => {
    if (!isGranted) return
    refreshChannels()
  }, [isGranted])

  const handleEdit = (channel: AIChannel) => {
    setEditing(channel)
    setForm({
      name: channel.name,
      apiKey: '',
      baseUrl: channel.base_url || '',
      tier: channel.tier || 'standard',
      priority: String(channel.priority ?? 1),
      enabled: channel.enabled,
      isOfficial: channel.is_official,
      maxFailCount: String(channel.max_fail_count ?? 2),
      recoveryTime: String(channel.recovery_time ?? 300),
      description: channel.description || ''
    })
    setAlert('')
  }

  const handleCancelEdit = () => {
    setEditing(null)
    setForm(createDefaultForm())
  }

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      setAlert(t('admin.aiChannels.validationName'))
      return
    }
    if (!editing && !form.apiKey.trim()) {
      setAlert(t('admin.aiChannels.validationKey'))
      return
    }

    setSubmitting(true)
    setAlert('')

    const payload: Record<string, any> = {
      name: form.name.trim(),
      base_url: form.baseUrl.trim(),
      tier: form.tier.trim(),
      priority: Number(form.priority) || 1,
      enabled: form.enabled,
      is_official: form.isOfficial,
      max_fail_count: Number(form.maxFailCount) || 2,
      recovery_time: Number(form.recoveryTime) || 300,
      description: form.description.trim()
    }

    if (form.apiKey.trim()) {
      payload.api_key = form.apiKey.trim()
    }

    try {
      if (editing) {
        await requestApi(`/api/v1/admin/ai-channels/${editing.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload)
        })
        setAlert(t('admin.aiChannels.updated'))
      } else {
        await requestApi('/api/v1/admin/ai-channels', {
          method: 'POST',
          body: JSON.stringify(payload)
        })
        setAlert(t('admin.aiChannels.created'))
      }
      handleCancelEdit()
      refreshChannels()
    } catch (err) {
      setAlert(err instanceof Error ? err.message : t('admin.aiChannels.error'))
    } finally {
      setSubmitting(false)
    }
  }

  const handleToggle = async (channel: AIChannel) => {
    try {
      await requestApi(`/api/v1/admin/ai-channels/${channel.id}/toggle`, {
        method: 'PATCH',
        body: JSON.stringify({ enabled: !channel.enabled })
      })
      refreshChannels()
    } catch (err) {
      setAlert(err instanceof Error ? err.message : t('admin.aiChannels.error'))
    }
  }

  const handleTest = async (channel: AIChannel) => {
    try {
      const payload = await requestApi<{ message?: string }>(`/api/v1/admin/ai-channels/${channel.id}/test`, {
        method: 'POST'
      })
      setAlert(payload?.message || t('admin.aiChannels.tested'))
    } catch (err) {
      setAlert(err instanceof Error ? err.message : t('admin.aiChannels.error'))
    }
  }

  const handleDelete = async (channel: AIChannel) => {
    const confirmed = window.confirm(t('admin.aiChannels.deleteConfirm').replace('{name}', channel.name))
    if (!confirmed) return
    try {
      await requestApi(`/api/v1/admin/ai-channels/${channel.id}`, { method: 'DELETE' })
      refreshChannels()
    } catch (err) {
      setAlert(err instanceof Error ? err.message : t('admin.aiChannels.error'))
    }
  }

  const statusBadge = useMemo(
    () => (status?: string | null) => {
      if (!status) return 'bg-slate-100 text-slate-400'
      if (status === 'healthy') return 'bg-emerald-100 text-emerald-700'
      if (status === 'degraded') return 'bg-amber-100 text-amber-700'
      if (status === 'failed') return 'bg-rose-100 text-rose-700'
      if (status === 'disabled') return 'bg-slate-200 text-slate-500'
      return 'bg-slate-100 text-slate-500'
    },
    []
  )

  const summary = useMemo(() => {
    const total = channels.length
    const enabled = channels.filter((item) => item.enabled).length
    const degraded = channels.filter((item) => item.status === 'degraded' || item.status === 'failed').length
    const totalRequests = channels.reduce((sum, item) => sum + (item.total_requests || 0), 0)
    const totalFailures = channels.reduce((sum, item) => sum + (item.total_failures || 0), 0)
    const failureRate = totalRequests ? (totalFailures / totalRequests) * 100 : 0
    return { total, enabled, degraded, totalRequests, failureRate }
  }, [channels])

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        {lastUpdated && (
          <p className="text-xs text-slate-400">{t('admin.aiChannels.updatedAt').replace('{time}', lastUpdated)}</p>
        )}
        <button
          onClick={refreshChannels}
          className="px-3 py-2 text-xs font-semibold border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50"
        >
          {t('admin.aiChannels.refresh')}
        </button>
      </div>
        <div className="border border-slate-200 rounded-2xl p-4 bg-slate-50/70">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold text-slate-700">{editing ? t('admin.aiChannels.edit') : t('admin.aiChannels.create')}</h3>
              <p className="text-xs text-slate-400">{t('admin.aiChannels.formHint')}</p>
            </div>
            {editing && (
              <button onClick={handleCancelEdit} className="text-xs font-semibold text-slate-500 hover:text-slate-700">
                {t('admin.aiChannels.cancel')}
              </button>
            )}
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
            <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
              <p className="text-[10px] uppercase tracking-widest text-slate-400">{t('admin.aiChannels.summary.total')}</p>
              <p className="text-sm font-semibold text-slate-700">{summary.total}</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
              <p className="text-[10px] uppercase tracking-widest text-slate-400">{t('admin.aiChannels.summary.enabled')}</p>
              <p className="text-sm font-semibold text-slate-700">{summary.enabled}</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
              <p className="text-[10px] uppercase tracking-widest text-slate-400">{t('admin.aiChannels.summary.degraded')}</p>
              <p className="text-sm font-semibold text-slate-700">{summary.degraded}</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
              <p className="text-[10px] uppercase tracking-widest text-slate-400">{t('admin.aiChannels.summary.requests')}</p>
              <p className="text-sm font-semibold text-slate-700">{summary.totalRequests}</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
              <p className="text-[10px] uppercase tracking-widest text-slate-400">{t('admin.aiChannels.summary.failureRate')}</p>
              <p className="text-sm font-semibold text-slate-700">{summary.failureRate.toFixed(1)}%</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-[11px] font-bold uppercase text-slate-400 tracking-widest">{t('admin.aiChannels.fields.name')}</label>
              <input
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder={t('admin.aiChannels.placeholders.name')}
              />
            </div>
            <div className="space-y-2">
              <label className="text-[11px] font-bold uppercase text-slate-400 tracking-widest">{t('admin.aiChannels.fields.apiKey')}</label>
              <input
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                value={form.apiKey}
                onChange={(e) => setForm((prev) => ({ ...prev, apiKey: e.target.value }))}
                placeholder={editing ? t('admin.aiChannels.placeholders.apiKeyEdit') : t('admin.aiChannels.placeholders.apiKey')}
              />
            </div>
            <div className="space-y-2">
              <label className="text-[11px] font-bold uppercase text-slate-400 tracking-widest">{t('admin.aiChannels.fields.baseUrl')}</label>
              <input
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                value={form.baseUrl}
                onChange={(e) => setForm((prev) => ({ ...prev, baseUrl: e.target.value }))}
                placeholder={t('admin.aiChannels.placeholders.baseUrl')}
              />
            </div>
            <div className="space-y-2">
              <label className="text-[11px] font-bold uppercase text-slate-400 tracking-widest">{t('admin.aiChannels.fields.tier')}</label>
              <input
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                value={form.tier}
                onChange={(e) => setForm((prev) => ({ ...prev, tier: e.target.value }))}
                placeholder="standard"
              />
            </div>
            <div className="space-y-2">
              <label className="text-[11px] font-bold uppercase text-slate-400 tracking-widest">{t('admin.aiChannels.fields.priority')}</label>
              <input
                type="number"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                value={form.priority}
                onChange={(e) => setForm((prev) => ({ ...prev, priority: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <label className="text-[11px] font-bold uppercase text-slate-400 tracking-widest">{t('admin.aiChannels.fields.maxFail')}</label>
              <input
                type="number"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                value={form.maxFailCount}
                onChange={(e) => setForm((prev) => ({ ...prev, maxFailCount: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <label className="text-[11px] font-bold uppercase text-slate-400 tracking-widest">{t('admin.aiChannels.fields.recovery')}</label>
              <input
                type="number"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                value={form.recoveryTime}
                onChange={(e) => setForm((prev) => ({ ...prev, recoveryTime: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <label className="text-[11px] font-bold uppercase text-slate-400 tracking-widest">{t('admin.aiChannels.fields.description')}</label>
              <input
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                value={form.description}
                onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                placeholder={t('admin.aiChannels.placeholders.description')}
              />
            </div>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input
                  type="checkbox"
                  checked={form.enabled}
                  onChange={(e) => setForm((prev) => ({ ...prev, enabled: e.target.checked }))}
                />
                {t('admin.aiChannels.fields.enabled')}
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input
                  type="checkbox"
                  checked={form.isOfficial}
                  onChange={(e) => setForm((prev) => ({ ...prev, isOfficial: e.target.checked }))}
                />
                {t('admin.aiChannels.fields.isOfficial')}
              </label>
            </div>
          </div>

          {alert && <p className="mt-4 text-xs text-rose-500">{alert}</p>}

          <div className="mt-4">
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="px-4 py-2 bg-primary text-white rounded-lg text-xs font-semibold hover:bg-blue-700 disabled:opacity-60"
            >
              {submitting ? t('admin.aiChannels.saving') : t('admin.aiChannels.save')}
            </button>
          </div>
        </div>

        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-slate-700">{t('admin.aiChannels.listTitle')}</h3>
          {loading && <p className="text-sm text-slate-400">{t('admin.aiChannels.loading')}</p>}
          {error && <p className="text-sm text-rose-500">{error}</p>}
          {!loading && !error && channels.length === 0 && (
            <p className="text-sm text-slate-400">{t('admin.aiChannels.empty')}</p>
          )}
          {!loading && !error && channels.length > 0 && (
            <div className="overflow-x-auto border border-slate-200 rounded-2xl">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-400 uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-3 text-left">{t('admin.aiChannels.table.name')}</th>
                    <th className="px-4 py-3 text-left">{t('admin.aiChannels.table.status')}</th>
                    <th className="px-4 py-3 text-left">{t('admin.aiChannels.table.priority')}</th>
                    <th className="px-4 py-3 text-left">{t('admin.aiChannels.table.baseUrl')}</th>
                    <th className="px-4 py-3 text-left">{t('admin.aiChannels.table.requests')}</th>
                    <th className="px-4 py-3 text-left">{t('admin.aiChannels.table.failures')}</th>
                    <th className="px-4 py-3 text-left">{t('admin.aiChannels.table.successRate')}</th>
                    <th className="px-4 py-3 text-left">{t('admin.aiChannels.table.avgLatency')}</th>
                    <th className="px-4 py-3 text-left">{t('admin.aiChannels.table.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {channels.map((channel) => (
                    <tr key={channel.id} className="border-t border-slate-100">
                      <td className="px-4 py-3">
                        <div className="font-semibold text-slate-800">{channel.name}</div>
                        <div className="text-xs text-slate-400">{channel.api_key}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusBadge(channel.status)}`}>
                          {channel.status || t('admin.aiChannels.statusUnknown')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{channel.priority}</td>
                      <td className="px-4 py-3 text-slate-600">{channel.base_url || '-'}</td>
                      <td className="px-4 py-3 text-slate-600">{channel.total_requests ?? '-'}</td>
                      <td className="px-4 py-3 text-slate-600">{channel.total_failures ?? '-'}</td>
                      <td className="px-4 py-3 text-slate-600">{channel.success_rate || '-'}</td>
                      <td className="px-4 py-3 text-slate-600">
                        {channel.avg_latency_ms ? `${channel.avg_latency_ms} ms` : '-'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => handleEdit(channel)}
                            className="text-xs font-semibold text-primary hover:text-blue-700"
                          >
                            {t('admin.aiChannels.actions.edit')}
                          </button>
                          <button
                            onClick={() => handleToggle(channel)}
                            className="text-xs font-semibold text-slate-600 hover:text-slate-800"
                          >
                            {channel.enabled ? t('admin.aiChannels.actions.disable') : t('admin.aiChannels.actions.enable')}
                          </button>
                          <button
                            onClick={() => handleTest(channel)}
                            className="text-xs font-semibold text-emerald-600 hover:text-emerald-700"
                          >
                            {t('admin.aiChannels.actions.test')}
                          </button>
                          <button
                            onClick={() => handleDelete(channel)}
                            className="text-xs font-semibold text-rose-500 hover:text-rose-600"
                          >
                            {t('admin.aiChannels.actions.delete')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
  )
}

export const AdminAIChannelsScreen = () => {
  const { t } = useLanguage()

  return (
    <AdminLayout title={t('admin.aiChannels.title')} description={t('admin.aiChannels.desc')}>
      <AdminAIChannelsContent />
    </AdminLayout>
  )
}
